import { isSameDay } from "date-fns";
import { FC, memo, useMemo, useState } from "react";
import { useTable } from "react-table";

import { useGetOutagesForWeekQuery } from "src/api";
import { Table, TableBody, TableHeader, TableLayout } from "src/components";
import { useTranslation } from "src/translations";
import { type NormalisedSystemOutagesDto, type SortDirection, OutageState } from "src/types";
import { BE_DATE_FORMAT, getOffsetDate, localeFormat, useAppSelector } from "src/utils";
import { getActiveOutageType, getWeekOffset } from "../selectors";
import { useFirstDayOfWeek, useToday } from "../utils";
import { DateHeader, OutageCell, SystemCell, SystemHeader } from "./Cells";

export const OutagesTable: FC = () => {
    const { t } = useTranslation();
    const weekOffset = useAppSelector(getWeekOffset);
    const activeOutageType = useAppSelector(getActiveOutageType);
    const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
    const today = useToday();
    const firstDayOfWeek = useFirstDayOfWeek();
    const { data: outagesData, isFetching, currentData } = useGetOutagesForWeekQuery(weekOffset, { pollingInterval: 5000 });
    const isFetchingNewEntity = outagesData === undefined || (isFetching && outagesData !== currentData);

    const ongoingOutages = useMemo<NormalisedSystemOutagesDto[]>(() => {
        if (!outagesData || isFetchingNewEntity) return [];

        return outagesData
            .map((system) => ({
                ...system,
                outages: Object.fromEntries(
                    Object.entries(system.outages).map(([date, outages]) => [
                        date,
                        outages.filter((outage) => outage.issueState !== OutageState.Cancelled),
                    ]),
                ),
            }))
            .filter((system) => Object.values(system.outages).some((outages) => outages.length > 0));
    }, [outagesData, isFetchingNewEntity]);

    const filtredOutages = useMemo<NormalisedSystemOutagesDto[]>(() => {
        if (!activeOutageType) {
            return ongoingOutages;
        }

        return ongoingOutages
            .map((system) => ({
                ...system,
                outages: Object.fromEntries(
                    Object.entries(system.outages).map(([date, outages]) => [
                        date,
                        outages.filter((outage) => outage.category === activeOutageType),
                    ]),
                ),
            }))
            .filter((system) => Object.values(system.outages).some((outages) => outages.length > 0));
    }, [ongoingOutages, activeOutageType]);

    const sortedOutages = useMemo(
        () => [...filtredOutages]?.sort((a, b) => (sortDirection === "asc" ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name))),
        [sortDirection, filtredOutages],
    );

    const columns = useMemo(() => {
        const systemNameCol = {
            accessor: "name",
            Header: (
                <SystemHeader
                    sortDirection={sortDirection}
                    onSortDirectionChange={() => setSortDirection(sortDirection === "asc" ? "desc" : "asc")}
                />
            ),
            // @ts-ignore
            // eslint-disable-next-line react/no-unstable-nested-components
            Cell: ({ row }) => <SystemCell system={row.original} />,
            minWidth: 200,
        };
        const dayCols = [...Array(7).keys()].map((day, index) => {
            const offsetDate = getOffsetDate(firstDayOfWeek, index, weekOffset);
            const isHighlighted = isSameDay(today, offsetDate);

            return {
                // @ts-ignore
                accessor: (row) => row.outages[localeFormat(offsetDate, BE_DATE_FORMAT)],
                id: day.toString(),
                // eslint-disable-next-line react/no-unstable-nested-components
                Header: memo(() => <DateHeader date={offsetDate} isHighlighted={isHighlighted} />),
                // @ts-ignore
                // eslint-disable-next-line react/no-unstable-nested-components
                Cell: memo(({ value }) => <OutageCell outages={value} isSameDay={isHighlighted} />),
                minWidth: 90,
                isHighlighted,
            };
        });

        return [systemNameCol, ...dayCols];
    }, [firstDayOfWeek, weekOffset, today, sortDirection]);

    // @ts-ignore
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({ columns, data: sortedOutages });

    return (
        <TableLayout>
            <Table {...getTableProps()}>
                {/* @ts-ignore todo: types */}
                <TableHeader headerGroups={headerGroups} />
                <TableBody<NormalisedSystemOutagesDto>
                    getTableBodyProps={getTableBodyProps}
                    noDataMessage={t("dashboard.noOutages")}
                    rows={rows}
                    prepareRow={prepareRow}
                    isLoading={isFetchingNewEntity}
                />
            </Table>
        </TableLayout>
    );
};
